"use client";

import { Button } from "@/components/ui/button";
import { Link } from '@/navigation';

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { FAQ } from "@/components/faq/FAQ";
import { AnimatedSection } from "@/components/ui/animated-section";
import { PricingIcons } from "./PricingIcons";
import { useTranslations } from 'next-intl';

interface PricingData {
  bento: {
    title: string;
    items: Array<{ size: string; price: string }>;
  };
  round: {
    title: string;
    items: Array<{ size: string; price: string }>;
    twoTiered: {
      title: string;
      items: Array<{ size: string; price: string }>;
    };
  };
  heart: {
    title: string;
    items: Array<{ size: string; price: string }>;
    twoTiered: {
      title: string;
      items: Array<{ size: string; price: string }>;
    };
  };
  cupcakes: {
    title: string;
    items: Array<{ size: string; price: string }>;
  };
  fillings: Array<{ name: string; color: string; borderColor: string }>;
  flavors: Array<{ name: string; label: string }>;
}

interface PricingClientProps {
  locale: string;
  pricingData: PricingData;
  faqs: Array<{ question: string; answer: string }>;
}

export function PricingClient({ locale, pricingData, faqs }: PricingClientProps) {
  const t = useTranslations('pricing');

  return (
    <>
      {/* Hero Section */}
      <section className="relative">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <div className="text-left">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-3 leading-tight">
                {t('title')}
              </h2>
              <p className="hidden md:block text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-1 max-w-3xl">
                {t('description')}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Starting Prices Notice */}
      <section className="py-2 md:py-3">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{t('notes.title')}</span>{' '}
              {t('notes.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Banner */}
      <section className="pb-3 md:pb-4">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div>
          </div>
        </div>
      </section>

      {/* Main Pricing Tables Section */}
      <section className="pt-1 pb-8 md:pb-10">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div>
            
            <div className="grid md:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
              {/* Bento Cakes */}
              <AnimatedSection animation="fade-up" delay={150}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <PricingIcons icon="ChefHat" className="w-6 h-6 text-primary" />
                      <CardTitle className="text-xl md:text-2xl">{pricingData.bento.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-base md:text-lg">
                            {t('table.size')}
                          </TableHead>
                          <TableHead className="text-right text-base md:text-lg">
                            {t('table.price')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pricingData.bento.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="py-2 md:py-2.5 text-sm md:text-base">{item.size}</TableCell>
                            <TableCell className="py-2 md:py-2.5 text-right text-base md:text-lg font-bold text-primary">
                              {item.price}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </AnimatedSection>

              {/* Cupcakes */}
              <AnimatedSection animation="fade-up" delay={200}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <PricingIcons icon="Cake" className="w-6 h-6 text-primary" />
                      <CardTitle className="text-xl md:text-2xl">{pricingData.cupcakes.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-base md:text-lg">
                            {t('table.quantity')}
                          </TableHead>
                          <TableHead className="text-right text-base md:text-lg">
                            {t('table.price')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pricingData.cupcakes.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="py-2 md:py-2.5 text-sm md:text-base">{item.size}</TableCell>
                            <TableCell className="py-2 md:py-2.5 text-right text-base md:text-lg font-bold text-primary">
                              {item.price}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>

            {/* Round Cakes */}
            <AnimatedSection animation="fade-up" delay={250}>
              <Card className="mb-4 md:mb-5 hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <PricingIcons icon="Circle" className="w-6 h-6 text-primary" />
                    <CardTitle className="text-xl md:text-2xl">{pricingData.round.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">
                        {t('standard')}
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-base md:text-lg">
                              {t('table.size')}
                            </TableHead>
                            <TableHead className="text-right text-base md:text-lg">
                              {t('table.price')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pricingData.round.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="py-2 md:py-2.5 text-sm md:text-base">{item.size}</TableCell>
                              <TableCell className="py-2 md:py-2.5 text-right text-base md:text-lg font-bold text-primary">
                                {item.price}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="pt-3 border-t">
                      <h4 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">
                        {pricingData.round.twoTiered.title}
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-base md:text-lg">
                              {t('table.size')}
                            </TableHead>
                            <TableHead className="text-right text-base md:text-lg">
                              {t('table.price')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pricingData.round.twoTiered.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="py-2 md:py-2.5 text-sm md:text-base">{item.size}</TableCell>
                              <TableCell className="py-2 md:py-2.5 text-right text-base md:text-lg font-bold text-primary">
                                {item.price}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>

            {/* Heart Cakes */}
            <AnimatedSection animation="fade-up" delay={300}>
              <Card className="mb-4 md:mb-5 hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <PricingIcons icon="Heart" className="w-6 h-6 text-primary" />
                    <CardTitle className="text-xl md:text-2xl">{pricingData.heart.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">
                        {t('standard')}
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-base md:text-lg">
                              {t('table.size')}
                            </TableHead>
                            <TableHead className="text-right text-base md:text-lg">
                              {t('table.price')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pricingData.heart.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="py-2 md:py-2.5 text-sm md:text-base">{item.size}</TableCell>
                              <TableCell className="py-2 md:py-2.5 text-right text-base md:text-lg font-bold text-primary">
                                {item.price}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="pt-3 border-t">
                      <h4 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">
                        {pricingData.heart.twoTiered.title}
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-base md:text-lg">
                              {t('table.size')}
                            </TableHead>
                            <TableHead className="text-right text-base md:text-lg">
                              {t('table.price')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pricingData.heart.twoTiered.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="py-2 md:py-2.5 text-sm md:text-base">{item.size}</TableCell>
                              <TableCell className="py-2 md:py-2.5 text-right text-base md:text-lg font-bold text-primary">
                                {item.price}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>

            {/* Options Sections */}
            <div className="grid md:grid-cols-2 gap-4 md:gap-5">
              {/* Cake Fillings */}
              <AnimatedSection animation="fade-up" delay={350}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                  <CardHeader>
                    <CardTitle className="text-xl md:text-2xl">
                      {t('fillings.title')}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {t('fillings.subtitle')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col md:flex-row md:flex-wrap gap-3">
                      {pricingData.fillings.map((filling, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div 
                            className={`w-6 h-6 rounded-full border-2 flex-shrink-0 ${filling.color} ${filling.borderColor}`}
                            aria-label={filling.name}
                          />
                          <Badge variant="outline" className="text-sm py-1.5 px-3">
                            {filling.name}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>

              {/* Cake Flavors */}
              <AnimatedSection animation="fade-up" delay={400}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                  <CardHeader>
                    <CardTitle className="text-xl md:text-2xl">
                      {t('flavors.title')}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {t('flavors.subtitle')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pricingData.flavors.map((flavor, index) => {
                        const isVanilla = flavor.name.toLowerCase().includes('vanilla') || flavor.name.toLowerCase().includes('vanille');
                        const colorClass = isVanilla 
                          ? 'bg-yellow-200 border-yellow-300' 
                          : 'bg-amber-900 border-amber-950';
                        return (
                          <div key={index} className="flex items-start gap-3">
                            <div 
                              className={`w-8 h-8 rounded-full border-2 flex-shrink-0 ${colorClass}`}
                              aria-label={flavor.name}
                            />
                            <div>
                              <p className="font-semibold text-base">{flavor.name}</p>
                              <p className="text-sm text-muted-foreground">{flavor.label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="py-8 md:py-10">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <AnimatedSection animation="fade-up" delay={450}>
              <FAQ
                faqs={faqs}
                variant="simple"
                title={t('faq.title')}
                sectionClassName=""
              />
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-8 md:py-10 pb-20 md:pb-10 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8 text-left">
          <AnimatedSection animation="scale" delay={0}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 md:mb-3">
              {t('cta.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-4 md:mb-6 opacity-90 max-w-3xl">
              {t('cta.description')}
            </p>
            <Button asChild size="lg" variant="secondary" className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 min-h-[44px]">
              <Link href="/order">
                {t('cta.button')}
              </Link>
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}
